import type { ExperienceEvent, FailEvent } from '@atlassian/experience-tracker/ExperienceEvent';
import type { CollectFn } from '@atlassian/experience-tracker/ExperienceTracker';

import { ExperienceName } from './constants';

export type RemixRegenerateSource = 'inline-existing-node';

type RemixRegenerateCollectContext = {
	conversationChannelId?: string;
	regenerateSource?: RemixRegenerateSource;
	allowCollectFallback?: boolean;
	targetIds?: {
		actionId?: string;
		contentMauiId?: string;
		mediaFileId?: string;
		selectionLocalId?: string;
		whiteboardContentId?: string;
	};
};

const SEND_MESSAGE_EXPERIENCE_NAMES = new Set<string>([ExperienceName.SEND_MESSAGE]);

const toError = (error: Error | undefined): Error => error ?? new Error('aiMateSendMessage failed');

const matchesSendMessageFailure = (
	event: ExperienceEvent,
	context: RemixRegenerateCollectContext,
): event is FailEvent => {
	if (!SEND_MESSAGE_EXPERIENCE_NAMES.has(event.name) || event.action !== 'taskFail') {
		return false;
	}

	const attributes = event.attributes as Record<string, unknown> | undefined;
	const isRemixRegenerate = attributes?.isRemixRegenerate === true;
	if (!isRemixRegenerate) {
		return false;
	}

	const requestSource = attributes?.remixRegenerateSource;
	if (context.regenerateSource && requestSource !== context.regenerateSource) {
		return false;
	}

	const requestConversationId = attributes?.conversationId;
	if (context.conversationChannelId && requestConversationId === context.conversationChannelId) {
		return true;
	}

	// For now, if allowCollectFallback is true, we will assume there's only one conversation stream at a time
	// and that this failure event is related to the inflight Remix request
	return Boolean(
		context.allowCollectFallback && context.regenerateSource === 'inline-existing-node',
	);
};

export const createRemixRegenerateCollect = (context: RemixRegenerateCollectContext): CollectFn => {
	return (events, experience): void => {
		for (const event of events) {
			if (!matchesSendMessageFailure(event, context)) {
				continue;
			}

			const attributes = event.attributes as Record<string, unknown> | undefined;
			experience.fail({
				error: toError(event.error),
				attributes: {
					...(attributes ?? {}),
					failureSource: 'send_message_collect',
					failureStage: 'request',
					upstreamExperience: event.name,
					...(context.regenerateSource ? { regenerateSource: context.regenerateSource } : {}),
					...(context.targetIds ?? {}),
				},
			});
			return;
		}
	};
};
